/**
 * Integrytas Group - Global Search Indexer (GSI)
 * Page: GSI.INDEXER
 *
 * Admin console for the audit-driven search index: the indexable-entity registry
 * (which entities are indexed + how), live worker/index stats, a one-click
 * reindex (replays the audit stream - never queries source tables), and a test
 * search that exercises the permission-filtered gsi_search RPC.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import type { AuthSession } from '../../types/auth';
import { useGlobalSearch } from '../../hooks/use_global_search';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'GSI.INDEXER';

interface EntityRow {
  gsi_indexable_entity_id: number;
  entity_name: string;
  page_code: string | null;
  title_field: string | null;
  text_fields: unknown;
  workspace_field: string | null;
  weight: number;
  is_active: 'Y' | 'N';
}
interface CursorRow { cursor_name: string; last_audit_log_id: number; last_run_at: string | null; processed_total: number }

export interface GsiIndexerPageProps { session: AuthSession }

export function GsiIndexerPage({ session }: GsiIndexerPageProps) {
  return (
    <PersonalizationProvider pageCode="GSI.INDEXER" session={session}>
      <GsiIndexerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function GsiIndexerPageInner({ session }: GsiIndexerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const ENTITY_GRID_COLS: GridColumnDef[] = [
    { key: 'entity_name',     header: t('gsi.col.entity', 'Entity'),                  defaultWidth: 180 },
    { key: 'page_code',       header: t('gsi.col.page_code', 'Page Code'),            defaultWidth: 140 },
    { key: 'title_field',     header: t('gsi.col.title_field', 'Title Field'),        defaultWidth: 130 },
    { key: 'text_fields',     header: t('gsi.col.text_fields', 'Text Fields'),        defaultWidth: 230 },
    { key: 'workspace_field', header: t('gsi.col.workspace_field', 'Workspace Field'), defaultWidth: 130 },
    { key: 'weight',          header: t('gsi.col.weight', 'Weight'),                  defaultWidth: 80  },
    { key: 'is_active',       header: t('gsi.col.active', 'Active'),                  defaultWidth: 70  },
  ];
  const [rows, setRows] = useState<EntityRow[]>([]);
  const [cursor, setCursor] = useState<CursorRow | null>(null);
  const [indexCount, setIndexCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §17.B13 related-children disclosure. Indexable-entity rows carry no per-row
  // child collection here, so the control renders inert until relations are wired.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);
  const [filter, setFilter] = useState('');
  // §17.B5/B12: per-field facet — default to Active-only on first load.
  const [facets, setFacets] = useState<{ active: string[] }>({ active: ['Y'] });
  const search = useGlobalSearch();

  const facetOptions = useMemo(() => ({
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
  }), [t]);

  const anyFilterActive = filter.trim().length > 0 || facets.active.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ active: [] }); };

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [e, c, n] = await Promise.all([
        apiDb.from('gsi_indexable_entity').select('gsi_indexable_entity_id, entity_name, page_code, title_field, text_fields, workspace_field, weight, is_active').order('entity_name'),
        apiDb.from('gsi_cursor').select('cursor_name, last_audit_log_id, last_run_at, processed_total').eq('cursor_name', 'main').maybeSingle(),
        apiDb.from('gsi_index').select('gsi_index_id', { head: true, count: 'exact' }),
      ]);
      if (e.error) throw e.error;
      setRows((e.data ?? []) as EntityRow[]);
      setCursor((c.data ?? null) as CursorRow | null);
      setIndexCount(n.count ?? null);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one.
    const matchesActive = (r: EntityRow) => facets.active.length === 0 || facets.active.includes(r.is_active);
    return rows.filter(r => {
      if (q && !((r.entity_name ?? '').toLowerCase().includes(q) || (r.page_code ?? '').toLowerCase().includes(q))) return false;
      return matchesActive(r);
    });
  }, [rows, filter, facets]);

  const addEntity = async () => {
    await routedInsert(session, 'gsi_indexable_entity', { entity_name: '', page_code: null, title_field: null, text_fields: [], workspace_field: null, weight: 1.0, is_active: 'N' });
    await reload();
  };
  const saveEntity = async (r: EntityRow, textFieldsJson: string) => {
    let tf: unknown; try { tf = JSON.parse(textFieldsJson); } catch { setError(t('gsi.text_fields_json', 'Text fields must be a JSON array, e.g. ["name","description"].')); return; }
    const { error: e } = await routedUpdate(session, 'gsi_indexable_entity', 'gsi_indexable_entity_id', r.gsi_indexable_entity_id, {
      entity_name: r.entity_name.trim(), page_code: r.page_code?.trim() || null, title_field: r.title_field?.trim() || null,
      text_fields: tf, workspace_field: r.workspace_field?.trim() || null, weight: r.weight, is_active: r.is_active,
    });
    if (e) setError(e.message);
  };
  const delEntity = async (id: number) => { await routedDelete(session, 'gsi_indexable_entity', 'gsi_indexable_entity_id', id); setRows(rs => rs.filter(x => x.gsi_indexable_entity_id !== id)); };

  const reindex = async () => {
    if (!confirm(t('gsi.reindex_confirm', 'Rebuild the entire search index by replaying the audit stream? Existing index rows are cleared and repopulated by the worker.'))) return;
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string; note?: string }>('gsi_reindex');
      if (e) throw e;
      if (data && data.ok === false) setError(data.error_code ?? t('gsi.reindex_refused', 'Reindex refused.'));
      await reload();
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('gsi.title', 'Global Search Indexer')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('gsi.filter_ph', 'Filter entities…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('common.boolean.active', 'Active')}
          options={facetOptions.active}
          selected={facets.active}
          onChange={v => setFacets(f => ({ ...f, active: v }))}
        />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('gsi.refresh', 'Refresh')}</button>
        {canChange && (<button onClick={() => void reindex()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('gsi.reindex', 'Reindex')}</button>)}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('gsi.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('gsi.error_banner', "Couldn't complete the last search-indexer operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex-1 overflow-auto p-3">
        <div className="mb-3 flex flex-wrap gap-4 text-csm-xs text-csm-ink-soft">
          <span>{t('gsi.indexed_records', 'Indexed records:')} <b className="text-csm-ink">{indexCount ?? '—'}</b></span>
          <span>{t('gsi.audit_cursor', 'Audit cursor:')} <b className="text-csm-ink">{cursor?.last_audit_log_id ?? '—'}</b></span>
          <span>{t('gsi.total_processed', 'Total processed:')} <b className="text-csm-ink">{cursor?.processed_total ?? '—'}</b></span>
          <span>{t('gsi.last_run', 'Last run:')} <b className="text-csm-ink">{cursor?.last_run_at ? new Date(cursor.last_run_at).toLocaleString() : '—'}</b></span>
        </div>

        <Section title={t('gsi.indexable_entities', 'Indexable Entities')} onAdd={addEntity}>
          <PersonalizedGrid componentKey="grid" columns={ENTITY_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="bg-csm-surface-alt text-csm-2xs uppercase tracking-wide text-csm-watermark">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-2 py-1" title={t('gsi.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                    ))}
                    <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('gsi.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const i = rows.indexOf(r);
                    return (
                      <Fragment key={r.gsi_indexable_entity_id}>
                      <tr>
                        <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detail.isOpen(r.gsi_indexable_entity_id)} hasChildren={hasRelated(r.gsi_indexable_entity_id)} onToggle={() => detail.toggle(r.gsi_indexable_entity_id)} expandLabel={t('gsi.related.expand', 'Show related records')} collapseLabel={t('gsi.related.collapse', 'Hide related')} tooltip={t('gsi.related.tooltip', 'Show related records')} />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 align-middle"
                              style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'entity_name'     && <input value={r.entity_name} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, entity_name: e.target.value } : x))} onBlur={() => void saveEntity(rows[i], JSON.stringify(rows[i].text_fields ?? []))} placeholder={t('gsi.entity_name_ph', 'entity_name')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                            {col.key === 'page_code'       && <input value={r.page_code ?? ''} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, page_code: e.target.value } : x))} onBlur={() => void saveEntity(rows[i], JSON.stringify(rows[i].text_fields ?? []))} placeholder={t('gsi.page_code_ph', 'PAGE.CODE')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" title={t('gsi.page_code_title', 'role-page-access gate')} />}
                            {col.key === 'title_field'     && <input value={r.title_field ?? ''} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, title_field: e.target.value } : x))} onBlur={() => void saveEntity(rows[i], JSON.stringify(rows[i].text_fields ?? []))} placeholder={t('gsi.title_field_ph', 'title')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" title={t('gsi.title_field_title', 'weight A')} />}
                            {col.key === 'text_fields'     && <input defaultValue={JSON.stringify(r.text_fields ?? [])} onBlur={e => void saveEntity(rows[i], e.target.value)} placeholder='["name","notes"]' className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('gsi.text_fields_title', 'searchable fields (weight B); DPC-masked fields are excluded automatically')} />}
                            {col.key === 'workspace_field' && <input value={r.workspace_field ?? ''} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, workspace_field: e.target.value } : x))} onBlur={() => void saveEntity(rows[i], JSON.stringify(rows[i].text_fields ?? []))} placeholder={t('gsi.workspace_field_ph', '(audit ws)')} className="h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-xs" title={t('gsi.workspace_field_title', 'override workspace source; blank = audit workspace_id')} />}
                            {col.key === 'weight'          && <input type="number" step="0.1" value={r.weight} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, weight: Number(e.target.value) } : x))} onBlur={() => void saveEntity(rows[i], JSON.stringify(rows[i].text_fields ?? []))} className="h-7 w-full rounded border border-csm-border px-1 text-csm-xs" title={t('gsi.weight_title', 'rank multiplier')} />}
                            {col.key === 'is_active'       && <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => { setRows(rs => rs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveEntity(rows[i], JSON.stringify(rows[i].text_fields ?? []))} /> {t('gsi.on', 'on')}</label>}
                          </td>
                        ))}
                        <td className="border-b border-csm-border-soft px-2 py-1 text-right">{canDelete && (<button onClick={() => void delEntity(r.gsi_indexable_entity_id)} className="text-csm-danger text-csm-xs">{t('gsi.del', 'del')}</button>)}</td>
                      </tr>
                      {detail.isOpen(r.gsi_indexable_entity_id) && (
                        <tr>
                          <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                            <RelatedChildrenPanel groups={buildRelatedGroups(r.gsi_indexable_entity_id)} session={session} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {filtered.length === 0 && !busy && (
                    <tr><td colSpan={visibleCols.length + 2} className="px-2 py-8 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={t('gsi.no_entities', 'No indexable entities.')}
                        action={anyFilterActive ? undefined : canAdd && (<button onClick={() => void addEntity()} className="mt-2 h-6 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('gsi.add', '+ Add')}</button>)}
                      />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </Section>

        <Section title={t('gsi.test_search', 'Test Search')}>
          <input value={search.query} onChange={e => search.setQuery(e.target.value)} placeholder={t('gsi.search_ph', 'Search across indexed records…')} className="h-8 w-full max-w-md rounded border border-csm-border px-2 text-csm-sm" />
          <div className="mt-1 text-csm-2xs text-csm-watermark">{t('gsi.search_scope', 'Filtered to your active workspace and role-page-access.')} {search.busy ? t('gsi.searching', 'Searching…') : t('gsi.hits', '{{count}} hit(s)', { count: search.hits.length })}{search.error ? ` — ${search.error}` : ''}</div>
          <table className="mt-2 w-full max-w-2xl border-collapse text-csm-sm">
            <tbody>
              {search.hits.map((h, i) => (
                <tr key={`${h.entity_type}:${h.record_pk}:${i}`} className="border-b border-csm-border-soft">
                  <td className="px-2 py-1 font-mono text-csm-xs text-csm-ink-soft">{h.entity_type}</td>
                  <td className="px-2 py-1 font-mono text-csm-xs">{h.record_pk}</td>
                  <td className="px-2 py-1">{h.title ?? <span className="text-csm-watermark">{t('gsi.no_title', '(no title)')}</span>}</td>
                  <td className="px-2 py-1 text-right font-mono text-csm-2xs text-csm-watermark">{h.rank.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd?: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-csm-sm font-semibold text-csm-ink">{title}</h3>
        {onAdd && <button onClick={onAdd} className="h-6 rounded border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt">{t('gsi.add', '+ Add')}</button>}
      </div>
      {children}
    </section>
  );
}
